import { ActionIcon, Group, HoverCard, Modal, Text, Tooltip, useMantineTheme } from '@mantine/core';
import { useCallback } from 'react';

import { useDisclosure, useHover } from '@mantine/hooks';

import { IconBook } from '@tabler/icons-react';

import { RequirementDropdown } from '../DropdownMenus';
import { type RequirementType, getRequirementIdentifierSpan } from '../../utils/requirementUtils';
import { type Proposal, type RequirementResponse } from '../../types/apiTypes';
import { ResponseSources } from '../Modals';
import { defaultModalProps } from '../../utils/mantineUtils';

import { withAnalyticsEvent } from '../../utils/analyticsUtils';
import { editorContentIsEmpty } from '../../utils/stringUtils';

interface Props {
  response: RequirementResponse
  onSelectRequirement?: (requirementUid: string) => void
  isSelected: boolean
  onOpenEditRequirement: () => void
  proposal: Proposal
  canEdit: boolean
  setCurrentRequirement: (req: RequirementType) => void
}
const OutlineEditorRequirement: React.FC<Props> = (props: Props) => {
  const { ref: hoveredRef, hovered } = useHover();
  const [responseSourcesOpened, responseSourcesHandlers] = useDisclosure();

  const { colors, other: colorVars } = useMantineTheme();
  const {
    onSelectRequirement, response, onOpenEditRequirement, setCurrentRequirement, isSelected, proposal, canEdit
  } = props;
  const sectionUid = response.proposal_section?.uid ?? '';
  const requirementText = response.requirement?.text ?? '';
  const requirementUid = response.requirement?.uid ?? '';
  const requirementIdentifier = response.requirement?.identifier ?? '';
  const canClickRequirement = onSelectRequirement !== undefined;
  const onClickRequirement = () => {
    if (canClickRequirement) {
      onSelectRequirement(requirementUid);
    }
  };

  const sectionData = proposal.sections?.map((section) => {
    return {
      title: section?.title,
      uid: section?.uid,
      childSections: section?.child_sections,
      parentSectionUid: section?.parent_proposal_section_uid ?? undefined
    };
  }) ?? [{ title: '', uid: '' }];
  const handleOpenEditRequirement = useCallback(() => {
    setCurrentRequirement({
      requirementText,
      requirementUid,
      requirementResponseUid: response.uid,
      requirementIdentifier,
      sectionUid,
      ordinal: response.ordinal ?? 1,
      updatedAt: response.updated_at ?? ''
    });
    onOpenEditRequirement();
  }, [response, onOpenEditRequirement]);

  const responseIsEmpty = editorContentIsEmpty(response.content);

  const viewSources = (
    <ActionIcon
      variant='subtle'
      onClick={withAnalyticsEvent(responseSourcesHandlers.open, 'ViewSources')}
      disabled={responseIsEmpty}
      c={responseIsEmpty ? undefined : colorVars.primaryIconColor}
    >
      <IconBook size={20} />
    </ActionIcon>);
  const showEditRequirement = canEdit && requirementText !== '';

  const icons =
    <Group gap={4}>
      <Tooltip label={responseIsEmpty ? 'Save to view sources' : 'Show sources'} position='top' offset={8}>
        {viewSources}
      </Tooltip>
      {showEditRequirement &&
        <RequirementDropdown
          sectionData={sectionData}
          handleOpenEditRequirement={handleOpenEditRequirement}
          requirementResponseOrdinal={response.ordinal ?? 1}
          requirementResponseUid={response.uid}
          sectionUid={sectionUid}
          requirementRemovalType='UNASSIGN'
          requirementUid={requirementUid}
          opportunityUid={proposal.opportunity?.uid ?? ''}
        />}
    </Group>;
  return (
    <>
      <Modal opened={responseSourcesOpened} {...defaultModalProps} size='90%' zIndex={1000}>
        <ResponseSources
          onClose={responseSourcesHandlers.close}
          requirementResponse={response}
        />
      </Modal>
      <HoverCard
        position='bottom-end'
        withinPortal={false}
        offset={-24}
        shadow="md"
        closeDelay={0}
        transitionProps={{ transition: undefined }}
      >
        <HoverCard.Target>
          <Group
            ref={hoveredRef}
            justify='space-between'
            gap={8}
            px={8}
            py={6}
            mx={4}
            style={{
              borderRadius: 8,
              backgroundColor: hovered ? colors.gray[1] : isSelected ? colors.lightPurple[2] : undefined
            }}
          >
            <Text
              maw='100%'
              onClick={onClickRequirement}
              fz='md'
              fw={600}
              style={{ cursor: canClickRequirement ? 'pointer' : undefined }}
            >
              {getRequirementIdentifierSpan(requirementIdentifier, 'md')}
              {requirementText}
            </Text>
          </Group >
        </HoverCard.Target>
        <HoverCard.Dropdown p={4} style={{
          border: `1px solid ${colors.gray[4]}`,
          borderRadius: 8
        }}>
          {icons}
        </HoverCard.Dropdown>
      </HoverCard>
    </>
  );
};

export default OutlineEditorRequirement;
